import { useEffect, useMemo, useState } from "react";
import { Images, ChevronLeft, ChevronRight, X } from "lucide-react";
import { api, API } from "@/lib/api";
import { BeforeAfterSlider } from "@/components/gallery/BeforeAfterSlider";

const src = (u) => `${API.replace(/\/api$/, "")}${u}`;

const Lightbox = ({ photos, index, setIndex }) => {
  const p = photos[index];
  useEffect(() => {
    const k = (e) => {
      if (e.key === "Escape") setIndex(null);
      if (e.key === "ArrowRight") setIndex((index + 1) % photos.length);
      if (e.key === "ArrowLeft") setIndex((index - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [index, photos.length, setIndex]);
  return (
    <div role="dialog" aria-modal="true" aria-label={p.title || p.alt} className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-4" onClick={() => setIndex(null)} data-testid="photo-lightbox">
      <button aria-label="Cerrar" className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white" data-testid="lightbox-close-btn"><X /></button>
      <button aria-label="Anterior" className="absolute left-2 rounded-full bg-white/10 p-2 text-white sm:left-6" onClick={(e) => { e.stopPropagation(); setIndex((index - 1 + photos.length) % photos.length); }} data-testid="lightbox-prev-btn"><ChevronLeft /></button>
      <figure className="max-h-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <img src={src(p.url)} alt={p.alt} width={p.width} height={p.height} className="max-h-[78vh] w-auto rounded-sm object-contain" />
        {(p.title || p.description) && <figcaption className="mt-3 text-white">{p.title && <p className="font-bold">{p.title}</p>}{p.description && <p className="text-sm text-white/70">{p.description}</p>}</figcaption>}
      </figure>
      <button aria-label="Siguiente" className="absolute right-2 rounded-full bg-white/10 p-2 text-white sm:right-6" onClick={(e) => { e.stopPropagation(); setIndex((index + 1) % photos.length); }} data-testid="lightbox-next-btn"><ChevronRight /></button>
    </div>
  );
};

const CategoryChips = ({ groups, value, onChange }) => (
  <div className="mb-8 flex flex-wrap gap-2" data-testid="photo-category-filter">
    {[{ id: "", name: "Todas" }, ...groups].map((c) => (
      <button key={c.id} onClick={() => onChange(c.id)} data-testid={`photo-filter-${c.id || "all"}`}
        className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${value === c.id ? "border-gold bg-gold text-black" : "border-line text-mute hover:border-gold"}`}>{c.name}</button>
    ))}
  </div>
);

export const PhotoGallery = ({ data: given }) => {
  const [data, setData] = useState(given || null);
  const [group, setGroup] = useState("");
  const [sub, setSub] = useState("");
  const [index, setIndex] = useState(null);
  useEffect(() => { if (!given) api.get("/gallery").then((r) => setData(r.data)).catch(() => setData({ photos: [], categories: [], before_after: [] })); }, [given]);
  const parentOf = useMemo(() => Object.fromEntries((data?.categories || []).map((c) => [c.id, c.parent_id || c.id])), [data]);
  const groups = useMemo(() => {
    const used = new Set((data?.photos || []).map((p) => parentOf[p.category_id]).filter(Boolean));
    return (data?.categories || []).filter((c) => !c.parent_id && used.has(c.id));
  }, [data, parentOf]);
  if (!data) return null;
  const photos = [...data.photos].sort((a, b) => (b.cover - a.cover) || (b.featured - a.featured))
    .filter((p) => !group || parentOf[p.category_id] === group).filter((p) => !sub || p.category_id === sub);
  const usedSubs = new Set(data.photos.map((p) => p.category_id));
  const subs = group ? data.categories.filter((c) => c.parent_id === group && usedSubs.has(c.id)) : [];
  const catName = Object.fromEntries(data.categories.map((c) => [c.id, c.name]));
  return (
    <>
      {(
        <section className="container-x pb-16" aria-labelledby="fotos-title" data-testid="photo-gallery-section">
          <p className="eyebrow flex items-center gap-2"><Images size={16} /> Galería fotográfica</p>
          <h2 id="fotos-title" className="mt-2 mb-6 text-3xl font-extrabold">Trabajos realizados en el taller</h2>
          {!data.photos.length && <p className="card-dark p-6 text-mute" data-testid="photo-gallery-empty">Muy pronto subiremos fotos de nuestros trabajos.</p>}
          {groups.length > 1 && <CategoryChips groups={groups} value={group} onChange={(g) => { setGroup(g); setSub(""); }} />}
          {subs.length > 0 && <div className="-mt-4"><CategoryChips groups={subs} value={sub} onChange={setSub} /></div>}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
            {photos.map((p, i) => (
              <figure key={p.id} className={`card-dark group overflow-hidden ${p.cover ? "col-span-2 row-span-2" : ""}`} data-testid={`gallery-photo-${p.id}`}>
                <button className="block w-full" onClick={() => setIndex(i)} aria-label={`Ampliar: ${p.title || p.alt}`}>
                  <img src={src(p.cover ? p.url : p.thumb_url)} alt={p.alt} loading="lazy" decoding="async" width={p.width} height={p.height}
                    className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </button>
                {(p.title || p.category_id) && <figcaption className="p-3"><p className="text-sm font-bold leading-snug">{p.title}</p>{p.category_id && <p className="text-xs text-mute">{catName[p.category_id]}</p>}</figcaption>}
              </figure>
            ))}
          </div>
          {index !== null && photos[index] && <Lightbox photos={photos} index={index} setIndex={setIndex} />}
        </section>
      )}
      {data.before_after.length > 0 && (
        <section className="container-x pb-16" aria-labelledby="antes-despues-title" data-testid="before-after-section">
          <p className="eyebrow">Resultados reales</p>
          <h2 id="antes-despues-title" className="mt-2 mb-6 text-3xl font-extrabold">Antes y después de la reparación</h2>
          <div className="grid gap-6 md:grid-cols-2">
            {data.before_after.map((b) => (
              <article key={b.id} className="card-dark overflow-hidden" data-testid={`before-after-${b.id}`}>
                <BeforeAfterSlider before={{ ...b.before, src: src(b.before.url) }} after={{ ...b.after, src: src(b.after.url) }} id={b.id} />
                <div className="p-5">
                  <h3 className="text-lg font-bold">{b.service}</h3>
                  {(b.brand || b.model) && <p className="text-sm text-gold">{[b.brand, b.model].filter(Boolean).join(" ")}</p>}
                  {b.description && <p className="mt-2 text-sm text-mute">{b.description}</p>}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </>
  );
};
